import { anyLeague } from "../../../classificators/league/index.js";
import { IterableDataProcessor, RankingStat } from "../../../entities/index.js";
import { DataSection, PitchingRankingStats, ResultDataNew, ScoringData, Out } from "../../../enums/index.js";
import EnumValueProvider from "../../../providers/EnumValueProvider.js";
import NumberFormatter from "../../../formatters/NumberFormatter.js";
import resultsSliceForPitcher from "../../../classificators/resultsSlice/resultsSliceForPitcher.js";
import satisfiesAll from "../../../classificators/satisfiesAll.js";
import pitchingScopeContext from "../../../classificators/resultsSlice/pitchingScopeContext.js";
import RankingStatCaseVerifier from "../../../validators/RankingStatCaseVerifier.js";
import sessionWithStatGrabScoring from "../../../classificators/session/sessionWithStatGrabScoring.js";
import classifiesAsPickoff  from '../../../classificators/outType/classifiesAsPickoff.js';
import RankingStatCalculatorBase from "../RankingStatCalculatorBase.js";

/**
 * A pickoff occurs between pitches when a pitcher throws a ball to a fielder, who eventually puts out or assists in retiring an opposing baserunner.
 * An illegal pickoff attempt results in a balk.
 * @see https://www.mlb.com/glossary/standard-stats/pickoff
 * @see https://baseball.fandom.com/wiki/Pickoff
 */
class NewPickoffsCalculator extends RankingStatCalculatorBase {
    constructor(
        customResultsProcessor, rankingStatCaseVerifier, numberFormatter,
        pickoffClassifier
    ) {
        if (typeof(customResultsProcessor) !== 'undefined' && !(customResultsProcessor instanceof IterableDataProcessor)) {
            throw new TypeError('customResultsProcessor must be an instance inheriting from IterableDataProcessor class');
        }
        if (typeof(numberFormatter) !== 'undefined' && !(numberFormatter instanceof NumberFormatter)) {
            throw new TypeError('numberFormatter must be an instance inheriting from NumberFormatter class');
        }
        if (typeof(pickoffClassifier) !== 'undefined' && typeof(pickoffClassifier) !== 'function') {
            throw new TypeError('pickoffClassifier must be an function or undefined');
        }

        super(
            [ PitchingRankingStats.Pickoffs ],
            rankingStatCaseVerifier || new RankingStatCaseVerifier(
                anyLeague,
                sessionWithStatGrabScoring,
                satisfiesAll(
                    pitchingScopeContext,
                    resultsSliceForPitcher
                )
            )
        );

        this.numberFormatter = numberFormatter || new NumberFormatter(3);
        this.resultsProcessor = customResultsProcessor || new IterableDataProcessor(
            new EnumValueProvider()
        );
        this.pickoffClassifier = pickoffClassifier || classifiesAsPickoff;
    }

    /**
     * Calculates given statistical parameter
     * @param {object} parameter - a parameter to calculate, object from Enum PitchingRankingStats, BattingRankingStats or GeneralRankingStats
     * @param {ResultsSlice} slice - result slice with scope
     * @param {object} session - session object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/session-v2.json
     * @param {object} league - league object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/league.json
     * @returns {RankingStat|null} RankingStat instance or null when calculation wasn't possible
     */
    calculateStat(parameter, slice, session, league) {
        const results = slice.getSmallestSlice();

        let pickoffs = 0;
        // @see https://www.mlb.com/glossary/standard-stats/pickoff
        // @see https://baseball.fandom.com/wiki/Pickoff

        for (let i = 0; i < results.length; i++) {
            const result = results[i];

            if (!result) {
                continue;
            }

            const outs = this.resultsProcessor.getObjectContent(
                result, ResultDataNew.getDataPath(ScoringData.Outs.key, DataSection.Scoring.key)
            );

            if (!outs || !outs.length) {
                continue;
            }

            // @see https://www.youtube.com/watch?v=X5QReya0vtA
            const pickoffOuts = outs.filter(o => this.pickoffClassifier(o[Out.Type.key]));
            pickoffs += pickoffOuts.length;
        }

        // now build stats
        if (pickoffs > 0) {
            const pickoffsParam = this.supportedParams[0];
            return RankingStat.fromParameter(
                pickoffsParam, this.numberFormatter.format(pickoffs), pickoffs
            );
        }

        return null;
    }
}

export default NewPickoffsCalculator;